import React from "react";
import Footer from "../Footer";
import Navbar from "../NavBar";
import { Helmet } from "react-helmet";
import SectionHeader from "../shared/SectionHeader";

const PrivacyPolicy = () => {
  return (
    <div className="text-gray-800">
      <Helmet>
        <title>Privacy Policy | Storage Sprint</title>
      </Helmet>

      <Navbar />

      <section className="py-28 bg-white">
        <div className="container mx-auto px-6">
          <SectionHeader
            title="Privacy"
            highlight="Policy"
            subtitle="Effective Date: 26/01/2025"
            className="mb-12"
          />
          
          <div className="max-w-5xl mx-auto space-y-8">
            <section className="bg-gray-50 rounded-xl p-8">
              <p className="text-gray-600">
                At <span className="font-bold text-gray-800">Storage Sprint</span> ("we," "our," or "us"), <span className="font-bold text-gray-800">your privacy is a priority</span>. This Privacy Policy outlines how we collect, use, and safeguard your personal information when using our website, app, or services (collectively, the "<span className="font-bold text-gray-800">Services</span>"). By accessing or using the Services, you agree to this policy.
              </p>
            </section>

            <section className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800">1. Information We Collect</h3>
              <div className="space-y-4 text-gray-600">
                <p><strong className="text-gray-800">a. Information You Provide</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Account Info:</strong> Name, email, phone number, password, and optional profile photo.</li>
                  <li><strong>Payment Details:</strong> Billing address and method. <span className="font-semibold text-gray-800">Note:</span> Payment data is processed via secure third-party services—we do not store this information.</li>
                  <li><strong>Storage & Delivery:</strong> Pickup and drop-off addresses, item descriptions, weight, and quantity.</li>
                </ul>

                <p><strong className="text-gray-800">b. Information Collected Automatically</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Usage Data:</strong> Order history, preferences, and feature interactions.</li>
                  <li><strong>Location Data:</strong> Real-time GPS location to support deliveries and pricing accuracy.</li>
                  <li><strong>Device Info:</strong> Device type, IP address, operating system, and app version.</li>
                </ul>

                <p><strong className="text-gray-800">c. From Third Parties</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Linked social accounts.</li>
                  <li>Public databases or business partners (for verification).</li>
                </ul>
              </div>
            </section>

            <section className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800">2. How We Use Your Information</h3>
              <ul className="list-disc list-inside ml-4 space-y-2 text-gray-600">
                <li>To provide, manage, and improve the Services.</li>
                <li>To process storage orders, payments, and deliveries.</li>
                <li>To calculate accurate pricing based on logistics.</li>
                <li>To send service updates, support messages, and marketing (with opt-out options).</li>
                <li>To prevent fraud, ensure security, and fulfill legal obligations.</li>
              </ul>
            </section>

            <section className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800">3. How We Share Your Information</h3>
              <div className="space-y-4 text-gray-600">
                <p>We <strong className="text-gray-800">do not sell</strong> your personal data. However, we may share it with:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Service Providers:</strong> Drivers, support staff, payment gateways.</li>
                  <li><strong>Business Partners:</strong> For joint offers and promotions.</li>
                  <li><strong>Legal Authorities:</strong> If legally required or to protect rights.</li>
                  <li><strong>Third-Party Tools:</strong> Like social media login integrations.</li>
                </ul>
              </div>
            </section>

            <section className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800">4. Data Security</h3>
              <p className="text-gray-600">
                We implement <strong className="text-gray-800">encryption, access controls</strong>, and regular audits to protect your data. However, no system is 100% secure.
              </p>
            </section>

            <section className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800">5. Your Rights</h3>
              <div className="space-y-4 text-gray-600">
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Access:</strong> Request your personal information.</li>
                  <li><strong>Correction:</strong> Fix inaccuracies.</li>
                  <li><strong>Deletion:</strong> Request removal of your data (when applicable).</li>
                  <li><strong>Objection:</strong> Limit data usage such as marketing.</li>
                  <li><strong>Portability:</strong> Export your data in a readable format.</li>
                </ul>
                <p>
                  To make a request, email us at: <a href="mailto:storagesprint@gmail.co.za" className="text-yellow-500 hover:underline">storagesprint@gmail.co.za</a>
                </p>
              </div>
            </section>

            <section className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800">6. Cookies & Tracking</h3>
              <div className="space-y-4 text-gray-600">
                <p>We use cookies to:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Enhance and personalize your experience.</li>
                  <li>Monitor app usage and performance.</li>
                  <li>Deliver relevant ads and content.</li>
                </ul>
                <p>You can manage cookie preferences via browser settings.</p>
              </div>
            </section>

            <section className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800">7. Data Retention</h3>
              <p className="text-gray-600">
                We keep your data only as long as needed to operate our services, resolve disputes, and meet legal obligations.
              </p>
            </section>

            <section className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800">8. Third-Party Links</h3>
              <p className="text-gray-600">
                Our Services may link to third-party websites. We are <strong className="text-gray-800">not responsible</strong> for their privacy practices. Please review their privacy policies independently.
              </p>
            </section>

            <section className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800">9. Children's Privacy</h3>
              <p className="text-gray-600">
                Our Services are intended for users <strong className="text-gray-800">18 years or older</strong>. We do not knowingly collect information from minors. If we become aware of this, we will delete it promptly.
              </p>
            </section>

            <section className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800">10. Changes to This Policy</h3>
              <p className="text-gray-600">
                We may update this Privacy Policy. Major changes will be communicated via the website or app. Continued use implies acceptance.
              </p>
            </section>

            <section className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800">11. Contact Us</h3>
              <div className="space-y-2 text-gray-600">
                <p>For any questions or concerns, please contact:</p>
                <p><strong>Storage Sprint</strong></p>
                <p>Email: <a href="mailto:storage.sprint@gmail.com" className="text-yellow-500 hover:underline">storage.sprint@gmail.com</a></p>
                <p>Phone: <strong>060 988 1456</strong></p>
              </div>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;